import { ChangeDetectorRef, Component, ElementRef, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { LoaderService } from '../../shared/services/loader.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService } from '../../shared/services/navigation.service';
import { Location } from '@angular/common';
import {
  ManualLocalLoadCreationItemRequest,
  ManualLocalLoadCreationRequest,
  ManualLocalLoadCreationShipmentRequest,
  ManualLocalLoadCreationStopRequest
} from '../shared/manual-local-load-creation';
import { Carrier } from '../../user-dashboard/shared/carrier.model';
import { CarrierService } from '../../user-dashboard/shared/carrier.service';
import { catchError, finalize, flatMap } from 'rxjs/operators';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';
import { LocalLoadService } from '../shared/local-load.service';
import { ModalService } from '../../shared/services/modal.service';
import { ManualLocalLocationCreationRequest } from '../shared/manual-local-location-creation';
import { LocalLocationService } from '../shared/local-location.service';
import { StopType } from '../../shared/stop-type.model';
import { LOCAL_STORAGE_KEYS } from '../../core/constants';
import { NgForm } from '@angular/forms';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { Observable, of } from 'rxjs';

@Component({
  selector: 'app-create-local-load',
  templateUrl: './create-local-load.component.html',
  styleUrls: ['./create-local-load.component.scss']
})
export class CreateLocalLoadComponent implements OnInit {

  request                                         = ManualLocalLoadCreationRequest.prepopulate();
  newShipment                                     = ManualLocalLoadCreationShipmentRequest.prepopulate();
  newLocation: ManualLocalLocationCreationRequest = new ManualLocalLocationCreationRequest();

  carriers: Carrier[] = [];
  selectedCarrier: Carrier;

  formProcessingBlocked = false;

  @ViewChild('localLoadForm')
  localLoadForm: NgForm;

  @ViewChild('addShipmentForm')
  addShipmentForm: NgForm;

  @ViewChild('addLocationForm')
  addLocationForm: NgForm;

  @ViewChild('addShipmentBtn')
  addShipmentBtn: ElementRef;

  @ViewChild('addShipmentSearchDropInput')
  addShipmentSearchDropInput: ElementRef;

  constructor(
    private carrierService: CarrierService,
    private localLoadService: LocalLoadService,
    private localLocationService: LocalLocationService,
    private loader: LoaderService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private navigation: NavigationService,
    private modal: ModalService,
    public authService: AuthenticationService,
    public location: Location,
    private errorHandler: ErrorHandlerService,
  ) {
    this.loader.startLoad(false);

    this.carrierService.getAll()
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(carriers => {
        const carrierIdFromLocalStorage = localStorage.getItem(LOCAL_STORAGE_KEYS.MANUAL_LOCAL_LOADS_CARRIER);

        this.carriers        = carriers;
        this.selectedCarrier = carriers.find(c => c.id === carrierIdFromLocalStorage);
      }, e => this.errorHandler.handleHttpError(e, 'local-loads.search.errors.get-carriers', false));
  }

  ngOnInit() {
    this.request.vehicle = localStorage.getItem(LOCAL_STORAGE_KEYS.MANUAL_LOCAL_LOADS_VEHICLE);
  }

  carrierChanged() {
    if (this.selectedCarrier) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.MANUAL_LOCAL_LOADS_CARRIER, this.selectedCarrier.id);
    }
  }

  processForm(): void {
    if (this.localLoadForm.invalid) {
      return;
    }

    this.blockForm();
    this.localLoadService.createManualLocalLoad(this.selectedCarrier.id, this.request)
      .pipe(finalize(() => this.unblockForm()))
      .subscribe(() => {
        this.toastr.success(this.translate.instant('local-loads.manual.form.successes.create'));

        const vehicle = this.request.vehicle;
        localStorage.setItem(LOCAL_STORAGE_KEYS.MANUAL_LOCAL_LOADS_VEHICLE, vehicle);

        this.request = ManualLocalLoadCreationRequest.prepopulate(vehicle);

        setTimeout(() => {
          this.addShipmentBtn.nativeElement.focus();
        }, 100);
      }, e => this.errorHandler.handleHttpError(e, 'local-loads.manual.form.errors.create'));
  }

  openAddShipment(content) {
    this.newShipment = ManualLocalLoadCreationShipmentRequest.prepopulate();

    this.modal.open({
      title: this.translate.instant('local-loads.manual.form.modals.add-shipment.title'),
      content,
      validateButton: {
        text: this.translate.instant('buttons.add'),
        icon: 'fa fa-plus',
        disabled: () => this.addShipmentForm && this.addShipmentForm.invalid,
      },

    }).subscribe(() => this.addShipment());
  }

  validateAddShipmentModal() {
    if (this.addShipmentForm.valid) {
      this.modal.triggerResolution();
    }
  }

  addShipment() {
    const pickStop = this.request.stops.find(s => s.type === StopType.Pick && s.location.id === this.newShipment.pickLocation.id);
    const dropStop = this.request.stops.find(s => s.type === StopType.Drop && s.location.id === this.newShipment.dropLocation.id);

    if (pickStop) {
      pickStop.shipments.push(this.newShipment);
    } else {
      const newPickStop     = new ManualLocalLoadCreationStopRequest();
      newPickStop.type      = StopType.Pick;
      newPickStop.shipments = [this.newShipment];
      newPickStop.location  = this.newShipment.pickLocation;

      if (dropStop) {
        const dropStopIndex = this.request.stops.indexOf(dropStop);
        this.request.stops.splice(dropStopIndex, 0, newPickStop);
      } else {
        this.request.stops.push(newPickStop);
      }
    }

    if (dropStop) {
      dropStop.shipments.push(this.newShipment);
    } else {
      const newDropStop     = new ManualLocalLoadCreationStopRequest();
      newDropStop.type      = StopType.Drop;
      newDropStop.shipments = [this.newShipment];
      newDropStop.location  = this.newShipment.dropLocation;
      this.request.stops.push(newDropStop);
    }

    this.request = this.request.clone();

    this.newShipment = ManualLocalLoadCreationShipmentRequest.prepopulate();

    this.addShipmentBtn.nativeElement.focus();
  }

  openAddLocation(content: TemplateRef<any>) {
    this.newLocation = new ManualLocalLocationCreationRequest();

    this.modal.open({
      title: this.translate.instant('local-loads.manual.form.modals.add-location.title'),
      content,
      validateButton: {
        text: this.translate.instant('buttons.add'),
        icon: 'fa fa-plus',
        disabled: () => this.addLocationForm && this.addLocationForm.invalid,
      },
      beforeResolve: () => this.addLocation(),
    }).subscribe(() => this.addLocation());
  }

  addLocation(): Observable<void> {
    this.blockForm();

    return this.localLocationService.createManualLocalLocation(this.newLocation)
      .pipe(
        finalize(() => this.unblockForm()),
        flatMap(() => {
          this.toastr.success(this.translate.instant('local-loads.manual.form.successes.create-location'));

          this.newLocation = new ManualLocalLocationCreationRequest();

          return of(null);
        }),
        catchError(e => this.errorHandler.rethrowHttpError(e, 'local-loads.search.errors.create-location')),
      );
  }

  addItem() {
    this.newShipment.items.push(ManualLocalLoadCreationItemRequest.prepopulate());
  }

  searchPickLocation(shipment: ManualLocalLoadCreationShipmentRequest) {
    this.blockForm();

    this.localLocationService.searchLocations(shipment.pickLocationSearchString)
      .pipe(finalize(() => this.unblockForm()))
      .subscribe(result => {
        console.log(result);
        shipment.pickLocationSearchResult = result;

        if (result) {
          shipment.pickLocation = result[0];

          if (result.length) {
            this.addShipmentSearchDropInput.nativeElement.focus();
          }
        }
      });
  }

  searchDropLocation(shipment: ManualLocalLoadCreationShipmentRequest) {
    this.blockForm();

    this.localLocationService.searchLocations(shipment.dropLocationSearchString)
      .pipe(finalize(() => this.unblockForm()))
      .subscribe(result => {
        console.log(result);
        shipment.dropLocationSearchResult = result;

        if (result) {
          shipment.dropLocation = result[0];
        }
      });
  }

  blockForm(smallLoader = true) {
    this.formProcessingBlocked = true;
    this.loader.startLoad(smallLoader);
  }

  unblockForm() {
    this.loader.stopLoad();
    this.formProcessingBlocked = false;
  }
}
